import { useState, useCallback } from 'react'

import { useProviderLoader } from '@/Providers/useProviderLoader'
import { useAuthHook } from '@/hooks/useAuthHook'
import { useAspirantsApi } from '@/services/api/aspirantsApi'
import { useInscriptionsApi } from '@/services/api/inscriptionsApi'
import { MessageLevel, ErrorHandlingStrategy } from '@/types/api'

interface ValidationPayload {
    programId: string
    admissionTypeId: string
    campusId: string
    jornadaId: string
    cycleId?: string
}

// Helper function to extract boolean value from API response
const extractBooleanValue = (response: any): boolean | undefined => {
    // Caso 1: La respuesta es directamente un booleano
    if (typeof response === 'boolean') {
        return response
    }

    // Caso 2: La respuesta es un objeto ApiResponse
    if (response && typeof response === 'object' && 'data' in response) {
        const data = response.data

        if (typeof data === 'boolean') {
            return data
        }

        if (typeof data === 'string') {
            return data.toLowerCase() === 'true'
        }
    }

    return undefined
}

// Helper function to show informative message for type "04"
const showType04Message = (
    setShowModal: (show: boolean) => void,
    setModalMessage: (message: string | null) => void,
    setModalTitle: (title: string | null) => void
): void => {
    setModalTitle('graduatedValidationTitle')
    setModalMessage(
        'Escogiste el tipo de ingreso “Graduado de otro programa”, lo que significa que deseas realizar una homologación de un programa de origen que cursaste previamente en la Universidad EAN. Sin embargo, te informamos que tu solicitud será validada por el equipo de Admisiones, quien verificará el cumplimiento de los requisitos correspondientes para continuar con el proceso.'
    )
    setShowModal(true)
}

// Helper function to handle validation for admission type "04"
const handleAdmissionType04Validation = async (
    idAspirant: number,
    aspirantsApi: any,
    setShowModal: (show: boolean) => void,
    setModalMessage: (message: string | null) => void,
    setModalTitle: (title: string | null) => void
): Promise<boolean> => {
    try {
        const response =
            await aspirantsApi.getisElegibleForGraduateToOtherProgram(
                idAspirant
            )
        const isEligible = extractBooleanValue(response)

        if (isEligible === false || isEligible === undefined) {
            showType04Message(setShowModal, setModalMessage, setModalTitle)

            return true // Permitir continuar
        }

        // Si es elegible (true), no mostrar modal, continuar normalmente
        return true
    } catch {
        showType04Message(setShowModal, setModalMessage, setModalTitle)

        return true // Permitir continuar
    }
}

// Helper function to handle validation for types "02" and "03"
const handleAdmissionType02Or03Validation = (
    isEligible: boolean | undefined,
    setShowModal: (show: boolean) => void
): boolean => {
    if (isEligible === false) {
        setShowModal(true)

        return false
    }

    return true
}

// Helper to determine validation type
const determineValidationType = (
    admissionTypeId: string,
    showLoader: (title: string, message: string) => void
): {
    isProgramValidation: boolean
    isAdmissionTypeValidation: boolean
} => {
    const isProgramValidation = !admissionTypeId || admissionTypeId === ''
    const isAdmissionTypeValidation = Boolean(
        admissionTypeId &&
            (admissionTypeId === '02' ||
                admissionTypeId === '03' ||
                admissionTypeId === '04')
    )

    if (isProgramValidation) {
        showLoader(
            'Validando programa',
            'Estamos validando nuestro sistema académico por información'
        )
    } else if (isAdmissionTypeValidation) {
        showLoader(
            'Validando elegibilidad',
            'Estamos verificando tu elegibilidad para este tipo de ingreso'
        )
    }

    return { isProgramValidation, isAdmissionTypeValidation }
}

// Helper to validate admission type 04
const validateType04 = async (
    idAspirant: number,
    aspirantsApi: any,
    setShowModal: (show: boolean) => void,
    setModalMessage: (message: string | null) => void,
    setModalTitle: (title: string | null) => void,
    hideLoader: () => void,
    isProgramValidation: boolean,
    isAdmissionTypeValidation: boolean
): Promise<boolean> => {
    const result = await handleAdmissionType04Validation(
        idAspirant,
        aspirantsApi,
        setShowModal,
        setModalMessage,
        setModalTitle
    )

    if (isProgramValidation || isAdmissionTypeValidation) {
        hideLoader()
    }

    return result
}

// Helper function to fetch the aspirant's document number (studnr)
const getStudnr = async (
    idAspirant: number,
    aspirantsApi: any
): Promise<string | undefined> => {
    try {
        const response = await aspirantsApi.getBasicData(idAspirant)
        const basicData = response?.data?.data ?? response?.data

        return basicData?.documentNumber
    } catch {
        return undefined
    }
}

// Helper to validate admission type "02" (transferencia interna) via SAP Datasphere
const validateInternalTransfer = async (
    studnr: string,
    programId: string,
    inscriptionsApi: any
): Promise<boolean | undefined> => {
    const response = await inscriptionsApi.getInternalTransferValidation(
        studnr,
        programId
    )
    const data = response?.data ?? response

    return data?.isValidInternalTransfer
}

// Helper to validate admission type "03" (programa simultáneo) via SAP Datasphere
const validateSimultaneousProgram = async (
    studnr: string,
    programId: string,
    inscriptionsApi: any
): Promise<boolean | undefined> => {
    const response = await inscriptionsApi.getSimultaneousProgramValidation(
        studnr,
        programId
    )
    const data = response?.data ?? response

    return data?.isValidSimultaneousProgram
}

// Helper to validate admission types 02 and 03
const validateType02Or03 = async (
    admissionTypeId: string,
    idAspirant: number,
    programId: string,
    aspirantsApi: any,
    inscriptionsApi: any,
    setShowModal: (show: boolean) => void,
    hideLoader: () => void,
    isProgramValidation: boolean,
    isAdmissionTypeValidation: boolean
): Promise<boolean> => {
    const studnr = await getStudnr(idAspirant, aspirantsApi)

    const isEligible = !studnr
        ? undefined
        : admissionTypeId === '02'
          ? await validateInternalTransfer(studnr, programId, inscriptionsApi)
          : await validateSimultaneousProgram(
                studnr,
                programId,
                inscriptionsApi
            )

    const result = handleAdmissionType02Or03Validation(isEligible, setShowModal)

    if (isProgramValidation || isAdmissionTypeValidation) {
        hideLoader()
    }

    return result
}

// Helper to validate admission type based on ID
const validateAdmissionType = async (
    admissionTypeId: string,
    idAspirant: number,
    programId: string,
    aspirantsApi: any,
    inscriptionsApi: any,
    setShowModal: (show: boolean) => void,
    setModalMessage: (message: string | null) => void,
    setModalTitle: (title: string | null) => void,
    hideLoader: () => void,
    isProgramValidation: boolean,
    isAdmissionTypeValidation: boolean
): Promise<boolean> => {
    if (
        admissionTypeId !== '02' &&
        admissionTypeId !== '03' &&
        admissionTypeId !== '04'
    ) {
        if (isProgramValidation || isAdmissionTypeValidation) {
            hideLoader()
        }

        return true
    }

    if (admissionTypeId === '04') {
        return await validateType04(
            idAspirant,
            aspirantsApi,
            setShowModal,
            setModalMessage,
            setModalTitle,
            hideLoader,
            isProgramValidation,
            isAdmissionTypeValidation
        )
    }

    if (admissionTypeId === '03' || admissionTypeId === '02') {
        return await validateType02Or03(
            admissionTypeId,
            idAspirant,
            programId,
            aspirantsApi,
            inscriptionsApi,
            setShowModal,
            hideLoader,
            isProgramValidation,
            isAdmissionTypeValidation
        )
    }

    if (isProgramValidation || isAdmissionTypeValidation) {
        hideLoader()
    }

    return true
}

// Helper to validate if the aspirant can request admission to the selected
// program: blocks when SAP already has a line for the same program
// (name similarity within the same modality) or when it cannot be verified
const validateProgramEligibility = async (
    idAspirant: number,
    programId: string,
    aspirantsApi: any,
    inscriptionsApi: any,
    setShowModal: (show: boolean) => void,
    setModalMessage: (message: string | null) => void,
    setModalTitle: (title: string | null) => void
): Promise<boolean> => {
    const blockUnverified = (): boolean => {
        setModalTitle('programEligibilityErrorTitle')
        setModalMessage(
            'No fue posible verificar tu elegibilidad para este programa. Por favor, intenta nuevamente más tarde.'
        )
        setShowModal(true)

        return false
    }

    try {
        const studnr = await getStudnr(idAspirant, aspirantsApi)

        if (!studnr) {
            return blockUnverified()
        }

        const response = await inscriptionsApi.getProgramEligibilityValidation(
            studnr,
            programId
        )
        const data = response?.data ?? response

        if (typeof data?.isEligible !== 'boolean') {
            return blockUnverified()
        }

        if (!data.isEligible && data.reason === 'RE_ENROLLMENT') {
            setModalTitle('reEnrollmentTitle')
            setModalMessage(
                'Registras historial académico como estudiante de la Universidad Ean, por lo cual tu solicitud corresponde a un proceso de reintegro. Te invitamos a ingresar al Portal de Reintegros y realizar allí tu solicitud.'
            )
            setShowModal(true)

            return false
        }

        if (!data.isEligible) {
            setModalTitle('programExistsInSapTitle')
            setModalMessage(
                data.matchedProgram
                    ? `Ya tienes un registro en SAP para el programa ${data.matchedProgram}. No puedes solicitar un nuevo proceso de admisión para este programa.`
                    : 'Ya tienes un registro para este programa en SAP. No puedes solicitar un nuevo proceso de admisión para este programa.'
            )
            setShowModal(true)

            return false
        }

        return true
    } catch {
        return blockUnverified()
    }
}

// Helper to handle errors
const handleValidationError = (
    error: any,
    hideLoader: () => void,
    isProgramValidation: boolean,
    isAdmissionTypeValidation: boolean,
    setShowModal: (show: boolean) => void
): boolean => {
    if (isProgramValidation || isAdmissionTypeValidation) {
        hideLoader()
    }

    if (error?.response?.status === 404) {
        return true
    }

    setShowModal(true)

    return true
}

export const useAcademicOfferingValidation = () => {
    const [isValidating, setIsValidating] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [modalMessage, setModalMessage] = useState<string | null>(null)
    const [modalTitle, setModalTitle] = useState<string | null>(null)
    const { idAspirant } = useAuthHook()
    const { showLoader, hideLoader } = useProviderLoader()

    const aspirantsApi = useAspirantsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })
    const inscriptionsApi = useInscriptionsApi({
        messageLevel: MessageLevel.ERRORS_AND_INFO_ONLY,
        errorHandling: ErrorHandlingStrategy.RETURN
    })

    const validateSelection = useCallback(
        async (payload: ValidationPayload): Promise<boolean> => {
            if (!idAspirant) {
                return true
            }

            setIsValidating(true)
            setModalMessage(null)
            setModalTitle(null)

            const { isProgramValidation, isAdmissionTypeValidation } =
                determineValidationType(payload.admissionTypeId, showLoader)

            try {
                if (isProgramValidation) {
                    const isEligible = await validateProgramEligibility(
                        idAspirant,
                        payload.programId,
                        aspirantsApi,
                        inscriptionsApi,
                        setShowModal,
                        setModalMessage,
                        setModalTitle
                    )

                    if (!isEligible) {
                        hideLoader()

                        return false
                    }
                }

                return await validateAdmissionType(
                    payload.admissionTypeId,
                    idAspirant,
                    payload.programId,
                    aspirantsApi,
                    inscriptionsApi,
                    setShowModal,
                    setModalMessage,
                    setModalTitle,
                    hideLoader,
                    isProgramValidation,
                    isAdmissionTypeValidation
                )
            } catch (error: any) {
                return handleValidationError(
                    error,
                    hideLoader,
                    isProgramValidation,
                    isAdmissionTypeValidation,
                    setShowModal
                )
            } finally {
                setIsValidating(false)
            }
        },
        [
            idAspirant,
            aspirantsApi,
            inscriptionsApi,
            showLoader,
            hideLoader,
            setShowModal
        ]
    )

    const resetModal = useCallback(() => {
        setShowModal(false)
        setModalMessage(null)
        setModalTitle(null)
    }, [])

    return {
        validateSelection,
        isValidating,
        showModal,
        setShowModal,
        modalMessage,
        modalTitle,
        resetModal
    }
}
